// src/pages/Wallet.jsx
import {
  BarChartOutlined,
  CreditCardOutlined,
  HistoryOutlined,
  QrcodeOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom/dist";
import { getSettingDetails } from "../../api/SettingApi";
import { claimBonus, getUserDetailsById, sendMoney } from "../../api/UserAPI";
import { useGlobalUI } from "../../context/GlobalUIProvider";
import AuthService from "../../session/AuthService";

import HomeScreen from "./HomeScreen";
import NotificationScreen from "./NotificationScreen";
import PaymentScreen from "./Payments/PaymentScreen";
import ProfileScreen from "./ProfileAndRelated/ProfileScreen";
import TransactionList from "./TransactionList";
import PaymentFlow from "./QR/PaymentFlow";
import WalletTopUp from "./WalletToup/WalletTopUp";
import ClaimBonus from "./WalletToup/ClaimBonus";
import { notificationsUnread } from "../../api/NotificationApi";

const { Text } = Typography;

const Wallet = () => {
  const [activeTab, setActiveTab] = useState("home");
  const navigate = useNavigate();
  const { showLoader, hideLoader, message } = useGlobalUI();

  // State
  const [userDetails, setUserDetails] = useState({});
  const [walletDetails, setWalletDetails] = useState({});
  const [walletList, setWalletList] = useState([]);
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [settingDetails, setSettingDetails] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState("cards"); // cards | sendMoney
  const [showProfile, setShowProfile] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const res = await notificationsUnread();
      setNotifications(res.data);
    } catch (err) {
      console.error("Error loading notifications:", err);
      message.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "home") {
      getUserDetails();
      getAllSettingDetails();
      loadNotifications();
    }
  }, [activeTab]);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    message.success("Logged out successfully!");
    navigate("/login");
  };

  const toggleBalance = () => setShowBalance((prev) => !prev);

  const getAllSettingDetails = async () => {
    try {
      const res = await getSettingDetails();
      if (res?.data.data) setSettingDetails(res.data.data);
    } catch {
      message.error("Could not load settings");
    }
  };

  const getUserDetails = () => {
    showLoader();
    const payload = { id: AuthService.getAuthId() };
    getUserDetailsById(payload)
      .then((res) => {
        if (res.data.status === 1) {
          setUserDetails(res.data.data.userDetails);
          setWalletList(res.data.data.walletDetails);
          setWalletDetails(
            res.data.data.walletDetails.find((e) => e.primaryWallet === true)
          );
          setTransactionDetails(res.data.data.transactionDetails);
        }
      })
      .finally(hideLoader);
  };

  const handleClaimBonus = () => {
    showLoader();
    claimBonus({ id: AuthService.getAuthId() })
      .then((res) => {
        if (res.data.status === 1) {
          getUserDetails();
          // message.success("Bonus Claimed successful!");
        } else {
          message.error(res.data.message || "An Error Occurred.");
        }
      })
      .finally(hideLoader);
  };

  const handleSendMoney = (mobile, amount) => {
    const availableBalance = parseFloat(walletDetails.totalBalance);
    const enteredAmount = parseFloat(amount);

    if (enteredAmount > availableBalance) {
      message.error("Insufficient Balance");
      return;
    }

    sendMoney({
      id: AuthService.getAuthId(),
      mobileNo: mobile,
      amountSend: enteredAmount,
    }).then((res) => {
      if (res.data.status === 1) {
        getUserDetails();
        message.success("Transaction Successful.");
        setView("cards");
      } else {
        message.error(res.data.message);
      }
    });
  };

  const recentPays = [
    { id: 1, name: "Starbucks coffee", amount: -20, date: "09 Dec, 3:09 PM" },
    { id: 2, name: "Starbucks coffee", amount: -20, date: "09 Dec, 3:09 PM" },
    { id: 3, name: "Starbucks coffee", amount: -20, date: "09 Dec, 3:09 PM" },
  ];

  const menuItems = [
    { key: "home", label: "Home", icon: <WalletOutlined /> },
    { key: "payments", label: "Payments", icon: <CreditCardOutlined /> },
    { key: "qrpay", label: "QR Pay", icon: <QrcodeOutlined /> },
    { key: "history", label: "History", icon: <HistoryOutlined /> },
    { key: "reports", label: "Reports", icon: <BarChartOutlined /> },
  ];

  if (showProfile) {
    return (
      <ProfileScreen
        onBack={() => setShowProfile(false)}
        user={userDetails}
        handleLogout={handleLogout}
        setUserDetails={setUserDetails} // 🔹 pass setter down
      />
    );
  }

  const renderTransactionItem = (item) => {
    const isCredit = item.toMobileNo === AuthService.getmobileNo();
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <Text strong>
            {item.transactionType === "BONUS"
              ? "Bonus"
              : item.transactionType === "FEE"
              ? "Fee"
              : "Transfer"}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {isCredit
              ? item.fromMobileNo
                ? `Sent from ${item.fromMobileNo}`
                : "Bonus"
              : `Sent to ${item.toMobileNo}`}
          </Text>
        </div>
        <div style={{ textAlign: "right" }}>
          <Text
            style={{ color: isCredit ? "#10b981" : "#ef4444", fontWeight: 600 }}
          >
            {isCredit ? "+" : "-"}{" "}
            {userDetails.nationality === "Indian" ? "₹" : "$"}
            {parseFloat(item.transactionAmount).toFixed(2)}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {dayjs(item.createdDate).format("DD MMM YYYY hh:mm")}
          </Text>
        </div>
      </div>
    );
  };

  if (expanded) {
    return (
      <TransactionList
        transactionDetails={transactionDetails}
        renderTransactionItem={renderTransactionItem}
        onClose={() => setExpanded(false)}
      />
    );
  }

  return (
    <div style={{ padding: "16px", background: "#f5f7fa", minHeight: "100vh" }}>
      {activeTab === "home" && (
        <HomeScreen
          userDetails={userDetails}
          walletDetails={walletDetails}
          transactionDetails={transactionDetails}
          showBalance={showBalance}
          toggleBalance={toggleBalance}
          getUserDetails={getUserDetails}
          recentPays={recentPays}
          expanded={expanded}
          setActiveTab={(e) => setActiveTab(e)}
          renderTransactionItem={renderTransactionItem}
          setShowProfile={setShowProfile}
          setView={setView}
          notifications={notifications}
        />
      )}
      {activeTab === "payments" && (
        <PaymentScreen
          userDetails={userDetails}
          walletDetails={walletDetails}
          walletList={walletList}
          settingDetails={settingDetails}
          customerType={userDetails.customerType}
          handleSendMoney={handleSendMoney}
          getUserDetails={getUserDetails}
          setParentTab={(e) => setActiveTab(e)}
        />
      )}

      {activeTab === "top-up" && (
        <WalletTopUp onBack={() => setActiveTab("home")} />
      )}

      {activeTab === "history" && (
        <TransactionList
          transactionDetails={transactionDetails}
          renderTransactionItem={renderTransactionItem}
          onClose={() => setExpanded(false)}
        />
      )}

      {activeTab === "qrpay" && (
        <PaymentFlow
          setView={setView}
          walletDetails={walletDetails}
          userDetails={userDetails}
          settingDetails={settingDetails}
          customerType={userDetails.customerType}
          onClose={() => setView("cards")}
          onScanSuccess={() => {
            // you can set transfer details or navigate to SendMoneyConfirm
            setView("cards");
          }}
          setParentTab={(e) => setActiveTab(e)}
        />
      )}

      {activeTab === "notifications" && (
        <NotificationScreen
          onBack={() => setActiveTab("home")}
          notifications={notifications}
          setNotifications={setNotifications}
          loading={loading}
        />
      )}

      {activeTab === "claim-bonus" && (
        <ClaimBonus
          onBack={() => setActiveTab("home")}
          userDetails={userDetails}
          onClaimBonus={handleClaimBonus} // parent handler that calls backend + refreshes user
        />
      )}

      {/* Bottom Nav */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#021B79",
          borderTop: "1px solid #eee",
          padding: "6px 0",
          display: "flex",
          justifyContent: "space-around",
          boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
        }}
      >
        {menuItems.map((item) => (
          <div
            key={item.key}
            onClick={() => setActiveTab(item.key)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              fontSize: 12,
              color: activeTab === item.key ? "#1890ff" : "white",
              cursor: "pointer",
              flex: 1,
            }}
          >
            <div style={{ fontSize: 20 }}>{item.icon}</div>
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Wallet;
